import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useStore } from '../../../../store';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { useLocalStorage } from '../../../providers/local-storage-provider/local-storage.context';
import { USER_ID } from '../../../../store/modules/user/user.constants';
import createMongoSort from '../../../../utils/create-mongo-sort';
import { IFilterSortContext } from './filter-sort-context';
import checkPermission from '../../../../utils/permissions';
import { ISavedReportFilter } from '../types';
import useQuery from '../../../../utils/useQuery';
import { mergeQueryParamFilters } from '../../edit-report/reportParamsHelpers';

export interface IFilter {
  field: string,
  type: string,
  value: string
}
export interface IInitialValues {
  from: string;
  to: string;
  fromDate?: string;
  toDate?: string;
  filters?: IFilter[],
  sorts?: string[],
  displayTimeFilter?: boolean,
}

export const useFilterSortContextHook = (reportKey?: string):IFilterSortContext => {
  const [findFilter, setFindFilter] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [seededKey, setSeededKey] = useState<string | undefined>(undefined);
  const [sortOpened, setSortOpened] = useState<boolean>(false);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const localStorageService = useLocalStorage();
  const onFilterDrawerHandler = () => {
    setOpenFilterDrawer((prev) => !prev);
  };
  const hasNotWritePermission = !checkPermission('reports', 'write');
  const [submit, setSubmit] = useState<boolean>(false);

  const queryParams = useQuery();
  const keys = queryParams.keys();
  let resultKeys = keys.next();
  // eslint-disable-next-line
    const paramsMap: any = {};
  while (!resultKeys.done) {
    paramsMap[resultKeys.value] = { $eq: queryParams.get(resultKeys.value) };
    resultKeys = keys.next();
  }

  const filtersFromQueryParams = Object.keys(paramsMap).length > 0 ? JSON.stringify(paramsMap) : '';

  const REPORT_INITIAL_VALUES: IInitialValues = {
    from: '',
    to: '',
    filters: [],
    sorts: [],
    displayTimeFilter: false,
  };

  const {
    executedReport,
    listsForReport,
  } = useStore();

  const {
    regex,
    quickRanges: reportQuickRanges,
  } = listsForReport.data;
  const {
    find: reportFiltersString, sort: reportSortsString, id: reportId, code: reportCode,
  } = executedReport.data;
  const { loaded: definitionLoaded } = executedReport;

  const userSetFilters = hasNotWritePermission ? localStorageService.get<ISavedReportFilter>('REPORT_FILTERS') : null;
  const matchingReport = userSetFilters?.reportId === reportId?.toString();

  const userFilters = matchingReport ? userSetFilters?.filters : null;
  const userSorts = matchingReport ? userSetFilters?.sorts : null;

  function validatePreset(preset: string | undefined, regexArr: string[]): boolean {
    if (!preset) return false;
    return regexArr.some((el) => {
      const regexObj = new RegExp(el);
      return regexObj.test(preset);
    });
  }
  const isNumber = (value: string) => /^[0-9]+$/.test(value);
  const definitionMatchesKey = (key: string): boolean => (isNumber(key)
    ? `${reportId}` === key
    : (reportCode || '').toUpperCase() === key.toUpperCase());
  const RANGE_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function validateTime(parent: any, value: string | undefined, regexArr: string[]) {
    if (!parent.displayTimeFilter) {
      return true;
    }

    if (!value || (value && value.trim() === '')) {
      return false;
    }

    return isNumber(value)
    || validatePreset(value, regexArr)
    || moment(value, RANGE_DATE_FORMAT, true).isValid();
  }

  const buildReportFormValidationSchema = (regexArr: string[]) => Yup.object().shape({
    from: Yup.string().test('test-from-field', '(not valid value)', function v(value) {
      return validateTime(this.parent, value, regexArr);
    }),
    to: Yup.string().test('test-to-field', '(not valid value)', function v(value) {
      return validateTime(this.parent, value, regexArr);
    }),
    filters: Yup.array().of(
      Yup.object().shape({
        field: Yup.string().required('(field is required)'),
        type: Yup.string().required('(type is required)'),
        value: Yup.string().required('(value is required)'),
      }),
    ),
    sorts: Yup.array().of(
      Yup.string(),
    ),
  });

  const [reportInitialValues, setReportInitialValues] = useState<IInitialValues>(
    REPORT_INITIAL_VALUES,
  );

  const onSubmitAction = (findValue:string, sortValue:string) => {
    setSubmit(true);
    setFindFilter(findValue);
    setSort(sortValue);
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createSortObj = (sorts: string[]) => {
    if (!sorts) return {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = sorts.reduce((accumulator: any, sortData: string) => {
      if (sortData !== '') {
        return { ...accumulator, ...JSON.parse(sortData) };
      }
      return { ...accumulator };
    }, {});
    return result;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onReportFormSubmit = async (values: any) => {
    // eslint-disable-next-line
    const ands: any[] = [];
    if (values.from && values.to && values.displayTimeFilter) {
      ands.push({
        time: {
          $gte: validatePreset(values.from, regex)
            ? `${values.from}`
            : moment(isNumber(values.from) ? parseInt(values.from, 10) : values.from).valueOf(),
          $lte: validatePreset(values.to, regex)
            ? `${values.to}`
            : moment(isNumber(values.to) ? parseInt(values.to, 10) : values.to).valueOf(),
        },
      });
    }

    if (values.filters) {
      values.filters.forEach(
        (f: IFilter) => {
          let filterValue = f.value;
          let filterType = f.type;
          if (f.field === 'uid') {
            filterValue = f.value.toUpperCase();
          }
          if (f.type === '$begins') {
            filterType = '$regex';
            filterValue = `^${f.value}.*`;
          }
          if (f.type === '$ends') {
            filterType = '$regex';
            filterValue = `.*${f.value}$`;
          }
          if (f.type === '$contains') {
            filterType = '$regex';
            filterValue = `.*${f.value}.*`;
          }
          ands.push({ [f.field]: { [filterType]: filterValue } });
        },
      );
    }

    let filtersPayload;
    if (ands.length === 0) {
      filtersPayload = '';
    } else if (ands.length === 1) {
      filtersPayload = JSON.stringify(ands[0]);
    } else {
      filtersPayload = JSON.stringify({ $and: ands });
    }

    // eslint-disable-next-line
    onSubmitAction(
      filtersPayload,
      values.sorts.length ? JSON.stringify(createSortObj(values.sorts)) : '',
    );
    setOpenFilterDrawer(false);
  };

  const formik = useFormik({
    initialValues: reportInitialValues,
    validationSchema: buildReportFormValidationSchema(regex),
    enableReinitialize: true,
    onSubmit: onReportFormSubmit,
  });

  // eslint-disable-next-line
  const isNotNullOrUndefined = (value: any) => !(value === null || value === undefined);
  const { userActions } = useActions();
  const userID: number | null = localStorageService.get(USER_ID);

  const isDataReady = useMemo(
    () => findFilter !== undefined
      && sort !== undefined
      && (reportKey === undefined || seededKey === reportKey),
    [findFilter, sort, seededKey, reportKey],
  );

  useEffect(() => {
    if (userID) {
      userActions.getCurrentUser(+userID);
    }
  }, []);

  const seedFromStoreDefinition = (): void => {
    const values = { ...REPORT_INITIAL_VALUES };

    const filtersString = submit ? reportFiltersString || filtersFromQueryParams
      : mergeQueryParamFilters(reportFiltersString, filtersFromQueryParams);
    const sortsString = reportSortsString;

    if (filtersString) {
      setFindFilter(filtersString);
      const reportFilters: IFilter[] = [];
      // eslint-disable-next-line
      const findObj: any = JSON.parse(filtersString);
      if (findObj.$and) {
      // eslint-disable-next-line
      findObj.$and.forEach((f: any) => {
          const field = Object.keys(f)[0];
          if (field === 'time') {
            const gte = f[field].$gte;
            const lte = f[field].$lte;
            if (isNotNullOrUndefined(gte) && isNotNullOrUndefined(lte)) {
              values.from = validatePreset(gte, regex)
                ? gte
                : moment(gte).format(RANGE_DATE_FORMAT);
              values.to = validatePreset(lte, regex)
                ? lte
                : moment(lte).format(RANGE_DATE_FORMAT);
            } else {
              const type = Object.keys(f[field])[0];
              const value = f[field][type];
              reportFilters.push({
                field,
                type,
                value,
              });
            }
          } else {
            const type = Object.keys(f[field])[0];
            const value = f[field][type];
            reportFilters.push({
              field,
              type,
              value,
            });
          }
        });
      } else {
      // eslint-disable-next-line
      for (const key in findObj) {
          if (key === 'time') {
            const timeFilter = findObj[key];

            if (isNotNullOrUndefined(timeFilter.$gte) && isNotNullOrUndefined(timeFilter.$lte)) {
              values.from = validatePreset(timeFilter.$gte, regex)
                ? timeFilter.$gte
                : moment(timeFilter.$gte).format(RANGE_DATE_FORMAT);
              values.to = validatePreset(timeFilter.$lte, regex)
                ? timeFilter.$lte
                : moment(timeFilter.$lte).format(RANGE_DATE_FORMAT);
            } else {
              const filterKey = Object.keys(timeFilter)[0];
              const customTimeFilter = {
                field: 'time',
                type: `${filterKey}`,
                value: timeFilter[filterKey],
              };
              reportFilters.push(customTimeFilter);
            }
          } else {
            const typeAndValue = Object.entries(findObj[key]);
            const value = typeAndValue[0][1] as string;
            reportFilters.push({
              field: key,
              type: typeAndValue[0][0],
              value,
            });
          }
        }
      }

      values.filters = reportFilters;
    } else {
      setFindFilter('');
    }

    const reportSorts = [];
    if (sortsString) {
      setSort(sortsString);
      const parsed = JSON.parse(sortsString);
      const sortKeys = Object.keys(parsed);
      if (sortKeys.length > 0) {
        const sorts = sortKeys.map((sortKey) => {
          const direction = parsed[sortKey];
          if (direction === 1) {
            return createMongoSort(sortKey, 'ASC');
          }
          return createMongoSort(sortKey, 'DESC');
        });

        reportSorts.push(...sorts);
      }
    } else {
      setSort('');
    }

    if (reportSorts.length === 0) {
      reportSorts.push('');
    }

    values.sorts = reportSorts;
    values.displayTimeFilter = (values.from !== '' && values.to !== '');

    setReportInitialValues(values);
  };

  useEffect(() => {
    if (reportKey !== undefined) return;
    if (submit) return;
    seedFromStoreDefinition();
  }, [reportFiltersString, reportSortsString, reportQuickRanges, findFilter, submit]);

  useEffect(() => {
    if (reportKey === undefined) return;
    if (submit) return;
    const matches = !!definitionLoaded && definitionMatchesKey(reportKey);
    if (!matches) {
      if (!definitionLoaded && seededKey === reportKey) return;
      if (findFilter !== undefined) setFindFilter(undefined);
      if (sort !== undefined) setSort(undefined);
      if (seededKey !== undefined) setSeededKey(undefined);
      return;
    }
    seedFromStoreDefinition();
    if (seededKey !== reportKey) setSeededKey(reportKey);
  }, [reportFiltersString, reportSortsString, reportQuickRanges, findFilter, submit,
    definitionLoaded, reportKey]);

  return {
    findFilter: findFilter as string,
    sort: sort as string,
    sortOpened,
    setSortOpened,
    openFilterDrawer,
    onFilterDrawerHandler,
    formik,
    createSortObj,
    userFilters,
    userSorts,
    isDataReady,
    submit,
  };
};
